import { Trans } from '@lingui/react'
import classNames from 'clsx'
import { arrayOf, bool, node, number, shape, string } from 'prop-types'
import { PureComponent } from 'react'
import { compose } from 'redux'

import { TENANT_IT } from 'shared/consts'
import {
  CROSSSELL_ATTR_ANALYTICS_NAME,
  CROSSSELL_CLASSNAME,
} from 'shared/consts/crosssellWidgets'
import logger from 'shared/services/logger'
import Button from 'views/components/atoms/Button'
import IconButtonArrowRight from 'views/components/atoms/Icon/IconButtonArrowRight'
import ButtonNavigation from 'views/components/molecules/ButtonNavigation'
import {
  MORE_PRODUCTS_XSELL_ID,
  VISIBLE_COUNT_DESKTOP_DEFAULT,
  VISIBLE_COUNT_MOBILE_DEFAULT,
  VISIBLE_COUNT_TABLET_DEFAULT,
} from 'views/consts'
import withMountPoints from 'views/enhancers/withMountPoints'
import withShopAndRuntimeConfig from 'views/enhancers/withShopAndRuntimeConfig'
import { isProductAvailable } from 'views/utils/productUtils'
import { mountPointsType } from 'views/utils/propTypes'

import SliderHeader from './SliderHeader'
import 'shared/experiments/components/newCsw/NewCswDesign.scss'
import './SliderHorizontal.scss'
import SliderItemsList from './SliderItemsList'
import 'views/containers/crosssell/CrosssellSliderCompact/ProductSliderItemCompact/ProductSliderItemCompact.scss'

class SliderHorizontal extends PureComponent {
  constructor(props) {
    super(props)

    this.state = {
      adaptStrategy: 'centeredBySlide',
    }
  }

  render() {
    const {
      publicRuntimeConfig: {
        publicConfig: { tenant },
      },
      adaptableArrows,
      centered,
      className,
      customCssClass,
      excludedProducts,
      headerClassName,
      headerTag,
      hideArrowsOnMobile,
      hideArrowsOnTablet,
      items,
      itemsForLazyLoad,
      mountPoints,
      moreLinkTitle,
      moreLinkUrl,
      navWithOpacity,
      noAutoFullWidth,
      noSlider,
      showBuyButton,
      stretch,
      target,
      thumbnailNav,
      title,
      titleIsHtml,
      visibleItemCount,
      visibleItemCountSmall,
      visibleItemCountTiny,
      wid,
      isSponsored,
      showMoreLinkUrl,
      withNavDots,
      gkAirArea,
      gkAirServiceId,
    } = this.props

    const { adaptStrategy } = this.state

    if (!items || !items.length) {
      return null
    }

    let moreLink = moreLinkUrl

    if (wid && showMoreLinkUrl) {
      const crossselProducts = items
        .map(({ props: { product } }) => product)
        .filter(Boolean)
      moreLink =
        moreLinkUrl || mountPoints.getSearchProductsList(crossselProducts)
    }

    const colCount =
      noSlider || items.length === 1
        ? 4
        : Math.min(items.length, visibleItemCount)

    const colSize = 12 / colCount
    const isGkAirArea = Boolean(gkAirArea)

    const sliderClassName = classNames(
      'o-SliderHorizontal o-SliderHorizontalNewDesign--3-lines-title',
      className,
      customCssClass,
      {
        'o-SliderHorizontal__nav-with-nav-dots': withNavDots,
        'o-SliderHorizontal__nav-with-opacity': navWithOpacity,
        [CROSSSELL_CLASSNAME]: isGkAirArea || wid,
      }
    )

    const sliderProps = {
      className: sliderClassName,
      'data-slider-adapt-strategy': adaptStrategy,
      'data-analytics': isGkAirArea
        ? 'gk-air-area'
        : wid && CROSSSELL_ATTR_ANALYTICS_NAME,
    }

    if (isGkAirArea) {
      sliderProps['data-crosssell-id'] = gkAirArea
      sliderProps['data-gk-air-area'] = gkAirArea
      sliderProps['data-gk-air-service-id'] = gkAirServiceId
      sliderProps['data-crosssell-title'] = title
      sliderProps['data-gk-air-area-title'] = title || null
      sliderProps['data-qa-id'] = 'cross-sell-widget'
      sliderProps['data-qa-widget-type'] = gkAirArea
      sliderProps.serviceid = gkAirServiceId
    } else {
      sliderProps['data-crosssell-id'] = wid
      sliderProps['data-crosssell-title'] = title
    }

    if (!noSlider) {
      sliderProps['data-clientside-hook'] = thumbnailNav
        ? 'slider__thumbnails'
        : 'slider'
      sliderProps['data-slider-slideby'] = visibleItemCount

      if (visibleItemCountSmall) {
        sliderProps['data-slider-slideby-small'] = visibleItemCountSmall
      }

      if (visibleItemCountTiny) {
        sliderProps['data-slider-slideby-tiny'] = visibleItemCountTiny
      }
    } else {
      sliderProps['data-slider-id'] = wid
      sliderProps['data-clientside-hook'] = 'slider'
    }

    if (excludedProducts) {
      sliderProps['data-slider-excluded-products'] = excludedProducts
    }

    const navigationClassName = additionalClassName =>
      classNames(
        `o-SliderHorizontal__nav u-transform-center--y ${additionalClassName}`,
        {
          'u-hidden@small-tiny': hideArrowsOnMobile,
          'u-hidden@medium': hideArrowsOnTablet,
          'o-SliderHorizontal__nav--hidden': adaptableArrows,
        }
      )

    const availableItems = items.filter(item => {
      const isStockStatusUndefined = item.stockStatus === undefined

      const isOutOfStock =
        typeof item.stockStatus === 'string'
          ? !isProductAvailable({ status: item.stockStatus })
          : !isProductAvailable(item.stockStatus)

      const disabled = isStockStatusUndefined ? false : isOutOfStock

      if (!disabled) {
        return item
      } else {
        logger.error(
          `Product with id ${item.id} is disabled because it is out of stock`
        )
      }
    })

    return (
      <div {...sliderProps}>
        {(title || moreLink) && (
          <SliderHeader
            title={title}
            titleIsHtml={titleIsHtml}
            moreLinkTitle={moreLinkTitle}
            moreLinkUrl={moreLink}
            target={target}
            className={headerClassName}
            headerTag={headerTag}
            isSponsored={isSponsored}
            wid={wid}
          />
        )}
        {!noSlider && (
          <>
            <ButtonNavigation
              direction="left"
              iconSize="large"
              name="product-slider-previous-button"
              data-clientside-hook={
                adaptableArrows
                  ? 'slider__prev adaptable__arrows'
                  : 'slider__prev'
              }
              isClientsideControlled
              className={navigationClassName('o-SliderHorizontal__previous')}
            >
              <Trans id="general.link.label.back" />
            </ButtonNavigation>
            <ButtonNavigation
              direction="right"
              iconSize="large"
              name="product-slider-next-button"
              data-clientside-hook={
                adaptableArrows
                  ? 'slider__next adaptable__arrows'
                  : 'slider__next'
              }
              isClientsideControlled
              className={navigationClassName('o-SliderHorizontal__next')}
            >
              <Trans id="general.link.label.forward" />
            </ButtonNavigation>
          </>
        )}
        <div
          className={classNames({
            'o-SliderHorizontal__wrapper': true,
            'o-SliderHorizontal__wrapper--noSlider': noSlider,
          })}
        >
          <SliderItemsList
            stretch={stretch}
            centered={centered}
            noSlider={noSlider}
            noAutoFullWidth={noAutoFullWidth}
            items={availableItems}
            colSize={colSize}
            showBuyButton={showBuyButton}
          />
          {itemsForLazyLoad && (
            <div
              data-clientside-hook={MORE_PRODUCTS_XSELL_ID}
              data-items={JSON.stringify(itemsForLazyLoad)}
              data-show-buy-button={showBuyButton}
              data-wid={wid}
            />
          )}
        </div>
        {itemsForLazyLoad && (
          <div className="o-SliderHorizontal__show-more u-text-align--center u-padding--top">
            <Button
              reverse
              inlineDesktopOnly
              data-clientside-hook="showMoreButton"
              isClientsideControlled
              name="show-more-products-button"
            >
              <Trans id="general.label.loadMoreProducts" />
              {tenant === TENANT_IT && (
                <IconButtonArrowRight className="a-icon-button-arrow-right" />
              )}
            </Button>
          </div>
        )}
      </div>
    )
  }
}

SliderHorizontal.propTypes = {
  ...SliderHeader.propTypes,
  adaptStrategy: string,
  adaptableArrows: bool,
  centered: bool,
  className: string,
  customCssClass: string,
  excludedProducts: string,
  headerClassName: string,
  headerTag: string,
  hideArrowsOnMobile: bool,
  hideArrowsOnTablet: bool,
  items: arrayOf(node),
  itemsForLazyLoad: arrayOf(shape({})),
  navWithOpacity: bool,
  mountPoints: mountPointsType.isRequired,
  noAutoFullWidth: bool,
  noSlider: bool,
  showBuyButton: bool,
  stretch: bool,
  target: string,
  thumbnailNav: bool,
  visibleItemCount: number,
  visibleItemCountSmall: number,
  visibleItemCountTiny: number,
  wid: number,
  withNavDots: bool,
  gkAirArea: string,
  gkAirServiceId: string,
}

SliderHorizontal.defaultProps = {
  ...SliderHeader.defaultProps,
  excludedProducts: null,
  adaptStrategy: null,
  adaptableArrows: false,
  centered: true,
  className: null,
  customCssClass: null,
  headerClassName: null,
  headerTag: 'h2',
  hideArrowsOnMobile: true,
  hideArrowsOnTablet: false,
  items: null,
  itemsForLazyLoad: null,
  navWithOpacity: true,
  noAutoFullWidth: false,
  noSlider: false,
  showBuyButton: false,
  stretch: false,
  target: null,
  thumbnailNav: null,
  visibleItemCount: VISIBLE_COUNT_DESKTOP_DEFAULT,
  visibleItemCountSmall: VISIBLE_COUNT_TABLET_DEFAULT,
  visibleItemCountTiny: VISIBLE_COUNT_MOBILE_DEFAULT,
  wid: null,
  withNavDots: false,
  gkAirArea: null,
  gkAirServiceId: '',
}

export default compose(
  withShopAndRuntimeConfig,
  withMountPoints
)(SliderHorizontal)
